import React, { useState, useRef } from "react";
import {
  Autocomplete,
  Avatar, Box, Button, CircularProgress, Divider, IconButton,
  MenuItem, Select, TextField, Tooltip, Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  useGetLeadQuery,
  useUpdateLeadStatusMutation,
  useAddLeadCommunicationMutation,
  useConvertLeadMutation,
  useCreateLeadMutation,
  useGetNoteTemplateQuery,
} from "../../../Api/attoDeskApi";
import { ILead, LeadStatus, ILeadCommunication, INoteTemplate, ICharterQuoteFields } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";

import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon      from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon      from "@mui/icons-material/PhoneAndroid";
import SmsOutlinedIcon        from "@mui/icons-material/Sms";
import NoteAltOutlinedIcon    from "@mui/icons-material/NoteAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RouteOutlinedIcon      from "@mui/icons-material/RouteOutlined";
import TagIcon                from "@mui/icons-material/Tag";
import SendIcon               from "@mui/icons-material/Send";
import OpenInNewIcon          from "@mui/icons-material/OpenInNew";
import PersonOutlineIcon      from "@mui/icons-material/PersonOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

dayjs.extend(relativeTime);

// ── Design tokens ──────────────────────────────────────────────────────────────
const BORDER = "#e2e8f0";

// Status pipeline: maps backend value → display config
const PIPELINE: { value: LeadStatus; label: string; color: string; bg: string; border: string }[] = [
  { value: "new",       label: "New",        color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
  { value: "contacted", label: "Contacted",  color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  { value: "qualified", label: "Follow-up",  color: "#f97316", bg: "#fff7ed", border: "#fed7aa" },
  { value: "converted", label: "Converted",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  { value: "lost",      label: "Closed",     color: "#64748b", bg: "#f8fafc", border: "#cbd5e1" },
];

const COMM_CONFIG = {
  note:  { label: "Note",  color: "#64748b", bg: "#f8fafc", icon: <NoteAltOutlinedIcon sx={{ fontSize: 13 }} /> },
  call:  { label: "Call",  color: "#f59e0b", bg: "#fffbeb", icon: <PhoneOutlinedIcon  sx={{ fontSize: 13 }} /> },
  email: { label: "Email", color: "#2f3891", bg: "#eef2ff", icon: <EmailOutlinedIcon  sx={{ fontSize: 13 }} /> },
  sms:   { label: "SMS",   color: "#0ea5e9", bg: "#f0f9ff", icon: <SmsOutlinedIcon    sx={{ fontSize: 13 }} /> },
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  school: "School Trip", wedding: "Wedding", corporate: "Corporate", other: "Other",
};

// ── Charter quote (Book Now form) ──────────────────────────────────────────────
// The two groups the website form itself uses, so staff read the enquiry in the
// order the customer filled it in. Special Instruction is shown on its own
// below, because it is prose, not a field.
const CHARTER_GROUPS: { title: string; fields: { key: keyof ICharterQuoteFields; label: string }[] }[] = [
  {
    title: "Bus Information",
    fields: [
      { key: "bus_type",          label: "Bus Type" },
      { key: "transfer_type",     label: "Transfer Type" },
      { key: "no_of_passengers",  label: "Number of Passengers" },
      { key: "company_groupname", label: "Company / Group Name" },
    ],
  },
  {
    title: "Trip Information",
    fields: [
      { key: "departure_date",      label: "Departure Date" },
      { key: "departure_time",      label: "Departure Time" },
      { key: "departure_address",   label: "Departure Address" },
      { key: "destination_address", label: "Destination Address" },
      { key: "return_date",         label: "Return Date" },
      { key: "return_time",         label: "Return Time" },
    ],
  },
];

/** A date key is rendered the way the rest of the page renders dates. */
const charterValue = (key: keyof ICharterQuoteFields, raw: string): string => {
  if (key !== "departure_date" && key !== "return_date") return raw;
  const d = dayjs(raw);
  return d.isValid() ? d.format("MMM D, YYYY") : raw;
};

// ── Charter Quote Details card ─────────────────────────────────────────────────
const CharterQuoteCard: React.FC<{ fields: ICharterQuoteFields }> = ({ fields }) => {
  const groups = CHARTER_GROUPS
    .map((g) => ({
      title: g.title,
      rows: g.fields
        .map((f) => ({ ...f, value: (fields[f.key] ?? "").toString().trim() }))
        .filter((f) => f.value !== ""),
    }))
    .filter((g) => g.rows.length > 0);

  const special = (fields.special_instruction ?? "").trim();

  // A lead with a form_type but no answers left shows nothing at all.
  if (groups.length === 0 && special === "") return null;

  return (
    <Box sx={{ bgcolor: "#ffffff", borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 3,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box sx={{ width: 4, height: 18, bgcolor: "#dc2626", borderRadius: 2 }} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Charter Quote Details
        </Typography>
      </Box>

      {groups.map((group) => (
        <Box key={group.title} sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
            {group.title}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
            {group.rows.map((row) => (
              <Box key={row.key as string} sx={{ bgcolor: "#f8fafc", border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", mt: 0.25, wordBreak: "break-word" }}>
                  {charterValue(row.key, row.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      {special !== "" && (
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75 }}>
            Special Instructions
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-line" }}>
            {special}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Pipeline step ──────────────────────────────────────────────────────────────
const PipelineStep: React.FC<{
  step: typeof PIPELINE[0];
  currentStatus: LeadStatus;
  index: number;
  total: number;
  onClick: () => void;
}> = ({ step, currentStatus, index, total, onClick }) => {
  const currentIdx  = PIPELINE.findIndex((p) => p.value === currentStatus);
  const isActive    = step.value === currentStatus;
  const isCompleted = index < currentIdx;

  return (
    <Box sx={{ flex: 1, position: "relative" }}>
      {/* Connector line */}
      {index < total - 1 && (
        <Box sx={{
          position: "absolute", top: 18, left: "50%", width: "100%", height: 2,
          bgcolor: isCompleted ? "#16a34a" : "#e2e8f0", zIndex: 0,
        }} />
      )}
      <Box
        onClick={onClick}
        sx={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
          cursor: "pointer", position: "relative", zIndex: 1,
          "&:hover .step-circle": { transform: "scale(1.1)", boxShadow: `0 0 0 4px ${step.bg}` },
        }}
      >
        {/* Circle */}
        <Box
          className="step-circle"
          sx={{
            width: 36, height: 36, borderRadius: "50%",
            bgcolor: isActive ? step.color : isCompleted ? "#16a34a" : "#f1f5f9",
            border: `2px solid ${isActive ? step.color : isCompleted ? "#16a34a" : BORDER}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s ease",
            boxShadow: isActive ? `0 0 0 4px ${step.bg}` : "none",
          }}
        >
          {isCompleted ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#ffffff" }} />
          ) : (
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: isActive ? "#ffffff" : "#94a3b8" }}>
              {index + 1}
            </Typography>
          )}
        </Box>
        {/* Label */}
        <Typography sx={{
          fontSize: 10, fontWeight: isActive ? 800 : 500, whiteSpace: "nowrap",
          color: isActive ? step.color : isCompleted ? "#16a34a" : "#94a3b8",
        }}>
          {step.label}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Main Detail Page ───────────────────────────────────────────────────────────
const EnquiryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [activeAction, setActiveAction] = useState<"note" | "call" | "email" | "sms" | null>(null);
  const [commMsg, setCommMsg]           = useState("");
  const [commSubject, setCommSubject]   = useState("");
  const [savingComm, setSavingComm]     = useState(false);
  const [converting, setConverting]     = useState(false);
  const [selectedNoteTemplateId, setSelectedNoteTemplateId] = useState<string>("");

  const { data: noteTemplateData } = useGetNoteTemplateQuery({ searchText: "" });
  const noteTemplates: INoteTemplate[] = Array.isArray(noteTemplateData?.data) ? (noteTemplateData!.data as INoteTemplate[]) : [];

  const isCreate = id === "create";
  const { data, isLoading, refetch } = useGetLeadQuery(Number(id), { skip: !id || isNaN(Number(id)) });
  const [updateStatus]  = useUpdateLeadStatusMutation();
  const [addComm]       = useAddLeadCommunicationMutation();
  const [convertLead]   = useConvertLeadMutation();
  const [createLead]    = useCreateLeadMutation();

  // ── Create form state (used only when isCreate) ────────────────────────────
  const [createForm, setCreateForm] = useState({
    firstname: "", lastname: "", phone: "", email: "",
    message: "", event_type: "", event_date: "", route_pickup: "", route_dropoff: "",
    source: "manual" as "manual" | "online" | "call",
  });
  const [createSaving, setCreateSaving]   = useState(false);
  const [createErrors, setCreateErrors]   = useState({ phone: "", email: "" });
  const [createTouched, setCreateTouched] = useState({ phone: false, email: false });

  // ── Address autocomplete (Nominatim / OpenStreetMap) ──────────────────────
  interface AddressOption { label: string; line1: string; line2: string; }
  const [pickupOptions,  setPickupOptions]  = useState<AddressOption[]>([]);
  const [dropoffOptions, setDropoffOptions] = useState<AddressOption[]>([]);
  const [pickupLoading,  setPickupLoading]  = useState(false);
  const [dropoffLoading, setDropoffLoading] = useState(false);
  const pickupTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropoffTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const PROVINCE_ABBR: Record<string, string> = {
    "Ontario": "ON", "Quebec": "QC", "British Columbia": "BC", "Alberta": "AB",
    "Manitoba": "MB", "Saskatchewan": "SK", "Nova Scotia": "NS", "New Brunswick": "NB",
    "Newfoundland and Labrador": "NL", "Prince Edward Island": "PE",
    "Northwest Territories": "NT", "Yukon": "YT", "Nunavut": "NU",
  };

  const formatNominatimAddress = (r: any): AddressOption => {
    const a = r.address || {};
    const streetNumber = a.house_number || "";
    const route = a.road || "";
    const city = a.city || a.town || a.village || a.hamlet || "";
    const stateName = a.state || "";
    const province = a["ISO3166-2-lvl4"]?.split("-")[1] || PROVINCE_ABBR[stateName] || stateName;
    const postal = a.postcode || "";

    const street = [streetNumber, route].filter(Boolean).join(" ");
    const line1 = street || city;
    const line2Parts = [street ? city : "", province, postal].filter(Boolean);
    const line2 = line2Parts.join(", ");
    const label = [street, city, province, postal].filter(Boolean).join(", ");
    return { label, line1, line2 };
  };

  const searchAddress = (
    q: string,
    setOptions: React.Dispatch<React.SetStateAction<AddressOption[]>>,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    timerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
  ) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 3) { setOptions([]); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res  = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=ca&addressdetails=1&limit=6`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        const seen = new Set<string>();
        const results: AddressOption[] = [];
        for (const r of data as any[]) {
          const opt = formatNominatimAddress(r);
          if (!seen.has(opt.label)) { seen.add(opt.label); results.push(opt); }
        }
        setOptions(results);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, 500);
  };

  const validateCreateField = (field: "phone" | "email", value: string): string => {
    if (field === "phone") {
      if (value && !/^\d+$/.test(value)) return "Only digits allowed.";
      if (value && value.length !== 10)  return "Must be exactly 10 digits.";
    }
    if (field === "email") {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Enter a valid email address.";
    }
    return "";
  };

  const handleCreate = async () => {
    if (!createForm.firstname.trim()) { showErrorMessage("First name is required."); return; }
    // Run full validation before submit
    const phoneErr = validateCreateField("phone", createForm.phone);
    const emailErr = validateCreateField("email", createForm.email);
    setCreateErrors({ phone: phoneErr, email: emailErr });
    setCreateTouched({ phone: true, email: true });
    if (phoneErr || emailErr) return;

    setCreateSaving(true);
    try {
      const res: any = await createLead({
        firstname:     createForm.firstname.trim(),
        lastname:      createForm.lastname.trim()      || null,
        phone:         createForm.phone.trim()         || null,
        email:         createForm.email.trim()         || null,
        message:       createForm.message.trim()       || null,
        event_type:    createForm.event_type           || null,
        event_date:    createForm.event_date           || null,
        route_pickup:  createForm.route_pickup.trim()  || null,
        route_dropoff: createForm.route_dropoff.trim() || null,
        source:        createForm.source,
        status:        "new",
      });
      if (res?.error || res?.data?.success === false) {
        showErrorMessage(res?.error?.data?.message || res?.data?.message || "Failed to create enquiry.");
      } else {
        showMessage("Enquiry created successfully.");
        navigate("/enquiry/list");
      }
    } catch (err: any) { showErrorMessage(err?.data?.message || "Something went wrong."); }
    finally { setCreateSaving(false); }
  };

  const lead: ILead | null = (data as any)?.data ?? null;

  const handleStatusChange = async (newStatus: LeadStatus) => {
    if (!lead || lead.status === newStatus) return;
    const res: any = await updateStatus({ id: lead.id, status: newStatus });
    if (res?.error || res?.data?.success === false) {
      showErrorMessage(res?.data?.message || "Failed to update status.");
    } else {
      showMessage(`Status → ${PIPELINE.find((p) => p.value === newStatus)?.label}`);
      refetch();
    }
  };

  const handleLogComm = async () => {
    if (!lead || !commMsg.trim() || !activeAction) return;
    setSavingComm(true);
    const payload: any = { id: lead.id, type: activeAction, message: commMsg.trim() };
    if (activeAction === "email" && commSubject.trim()) payload.subject = commSubject.trim();
    const res: any = await addComm(payload);
    if (res?.error || res?.data?.success === false) {
      showErrorMessage(res?.data?.message || "Failed to send.");
    } else {
      showMessage(res?.data?.message || "Logged successfully.");
      setCommMsg(""); setCommSubject(""); setActiveAction(null); refetch();
    }
    setSavingComm(false);
  };

  const handleConvert = async () => {
    if (!lead) return;
    setConverting(true);
    const res: any = await convertLead(lead.id);
    if (res?.error || res?.data?.success === false) {
      const msg = res?.error?.data?.message || res?.data?.message || "Failed to convert.";
      showErrorMessage(msg);
    } else {
      showMessage("Lead converted to customer.");
      refetch();
    }
    setConverting(false);
  };

  const handleConvertToQuote = () => {
    if (!lead) return;
    // If the lead has been converted and a real Customer was created, use its ID
    // so the Quote form auto-selects the customer and avoids the "Customer required" error.
    if (lead.customer_id) {
      navigate(`/quote/create/${lead.customer_id}`);
      return;
    }
    // Fallback: pre-fill contact fields without selecting a customer
    const params = new URLSearchParams({
      leadFirstName: lead.contact.firstname,
      leadLastName:  lead.contact.lastname   || "",
      leadPhone:     lead.contact.phone      || "",
      leadEmail:     lead.contact.email      || "",
      leadPickup:    lead.contact.route_pickup  || "",
      leadDropoff:   lead.contact.route_dropoff || "",
      leadEventDate: lead.contact.event_date    || "",
    });
    navigate(`/quote/create?${params.toString()}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isCreate) {
    const inputSx = {
      "& .MuiOutlinedInput-root": {
        fontSize: 13,
        borderRadius: "8px",
        bgcolor: "#f9fafb",
        "& fieldset": { borderColor: "#e5e7eb" },
        "&:hover fieldset": { borderColor: "#2f3891" },
        "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: "2px" },
        "&.Mui-focused": { bgcolor: "#ffffff" },
        transition: "background 0.2s ease",
      },
    };
    const selectSx = {
      fontSize: 13,
      borderRadius: "8px",
      bgcolor: "#f9fafb",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#2f3891" },
      "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: "2px" },
    };
    const labelSx = { fontSize: 12, fontWeight: 600, color: "#6b7280", mb: 0.5, display: "block" };
    const cardSx = {
      bgcolor: "#ffffff", borderRadius: "12px",
      border: "1px solid #f1f5f9",
      boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
      p: "20px 24px", mb: "16px",
    };
    const sectionTitleSx = { fontSize: 14, fontWeight: 700, color: "#1f2937", m: 0 };

    const cf = createForm;
    const set = (k: keyof typeof createForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { value: unknown }>) =>
      setCreateForm((p) => ({ ...p, [k]: (e as any).target.value }));

    return (
      <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", p: { xs: 2, md: 3 } }}>

        {/* ── Page header ── */}
        <Box sx={{ maxWidth: 860, mx: "auto", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton size="small" onClick={() => navigate("/enquiry/list")}
              sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: "#fff", "&:hover": { bgcolor: "#f1f5f9" } }}>
              <ArrowBackIcon sx={{ fontSize: 17 }} />
            </IconButton>
            <Box>
              <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, lineHeight: 1.2 }}>
                Enquiries &nbsp;/&nbsp; New Enquiry
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#0f172a", lineHeight: 1.3 }}>
                New Enquiry
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 860, mx: "auto" }}>

          {/* ── Contact Information ── */}
          <Box sx={cardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, pb: 1.5, borderBottom: "1px solid #f1f5f9" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <PersonOutlineIcon sx={{ fontSize: 15, color: "#2f3891" }} />
              </Box>
              <Typography sx={sectionTitleSx}>Contact Information</Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Box>
                <Typography sx={labelSx}>First Name <span style={{ color: "#ef4444" }}>*</span></Typography>
                <TextField fullWidth size="small" placeholder="First name" value={cf.firstname} onChange={set("firstname")} sx={inputSx} />
              </Box>
              <Box>
                <Typography sx={labelSx}>Last Name</Typography>
                <TextField fullWidth size="small" placeholder="Last name" value={cf.lastname} onChange={set("lastname")} sx={inputSx} />
              </Box>
              <Box>
                <Typography sx={labelSx}>Phone</Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="e.g. 6475550100"
                  value={cf.phone}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setCreateForm((p) => ({ ...p, phone: digits }));
                    if (createTouched.phone)
                      setCreateErrors((p) => ({ ...p, phone: validateCreateField("phone", digits) }));
                  }}
                  onBlur={() => {
                    setCreateTouched((p) => ({ ...p, phone: true }));
                    setCreateErrors((p) => ({ ...p, phone: validateCreateField("phone", cf.phone) }));
                  }}
                  error={createTouched.phone && !!createErrors.phone}
                  helperText={createTouched.phone ? createErrors.phone : ""}
                  sx={inputSx}
                />
              </Box>
              <Box>
                <Typography sx={labelSx}>Email</Typography>
                <TextField
                  fullWidth size="small"
                  type="email"
                  placeholder="name@company.com"
                  value={cf.email}
                  onChange={(e) => {
                    set("email")(e);
                    if (createTouched.email)
                      setCreateErrors((p) => ({ ...p, email: validateCreateField("email", e.target.value) }));
                  }}
                  onBlur={() => {
                    setCreateTouched((p) => ({ ...p, email: true }));
                    setCreateErrors((p) => ({ ...p, email: validateCreateField("email", cf.email) }));
                  }}
                  error={createTouched.email && !!createErrors.email}
                  helperText={createTouched.email ? createErrors.email : ""}
                  sx={inputSx}
                />
              </Box>
            </Box>
          </Box>

          {/* ── Event Details ── */}
          <Box sx={cardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, pb: 1.5, borderBottom: "1px solid #f1f5f9" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 15, color: "#2f3891" }} />
              </Box>
              <Typography sx={sectionTitleSx}>Event Details</Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
              <Box>
                <Typography sx={labelSx}>Event Type</Typography>
                <Select fullWidth size="small" value={cf.event_type} onChange={set("event_type") as any}
                  sx={selectSx} displayEmpty>
                  <MenuItem value="" sx={{ fontSize: 13, color: "#9ca3af" }}>Select type…</MenuItem>
                  {[
                    { value: "school",    label: "School Trip" },
                    { value: "wedding",   label: "Wedding" },
                    { value: "corporate", label: "Corporate" },
                    { value: "other",     label: "Other" },
                  ].map((v) => (
                    <MenuItem key={v.value} value={v.value} sx={{ fontSize: 13 }}>{v.label}</MenuItem>
                  ))}
                </Select>
              </Box>
              <Box>
                <Typography sx={labelSx}>Event Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={cf.event_date ? dayjs(cf.event_date) : null}
                    onChange={(newValue) =>
                      setCreateForm((p) => ({ ...p, event_date: newValue ? newValue.format("YYYY-MM-DD") : "" }))
                    }
                    format="MMM DD, YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        placeholder: "Select date…",
                        sx: {
                          ...inputSx,
                          "& .MuiSvgIcon-root": { color: "#6b7280", fontSize: 18 },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box>
                <Typography sx={labelSx}>Source</Typography>
                <Select fullWidth size="small" value={cf.source} onChange={set("source") as any} sx={selectSx}>
                  <MenuItem value="manual" sx={{ fontSize: 13 }}>Manual</MenuItem>
                  <MenuItem value="call"   sx={{ fontSize: 13 }}>Call</MenuItem>
                  <MenuItem value="online" sx={{ fontSize: 13 }}>Online</MenuItem>
                </Select>
              </Box>
            </Box>
          </Box>

          {/* ── Locations ── */}
          <Box sx={cardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, pb: 1.5, borderBottom: "1px solid #f1f5f9" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <RouteOutlinedIcon sx={{ fontSize: 15, color: "#2f3891" }} />
              </Box>
              <Typography sx={sectionTitleSx}>Locations</Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Box>
                <Typography sx={labelSx}>Pickup Location</Typography>
                <Autocomplete
                  freeSolo
                  options={pickupOptions}
                  getOptionLabel={(opt) => typeof opt === "string" ? opt : opt.label}
                  loading={pickupLoading}
                  inputValue={cf.route_pickup}
                  filterOptions={(x) => x}
                  onInputChange={(_, value) => {
                    setCreateForm((p) => ({ ...p, route_pickup: value }));
                    searchAddress(value, setPickupOptions, setPickupLoading, pickupTimer);
                  }}
                  onChange={(_, value) => {
                    if (value) {
                      const label = typeof value === "string" ? value : value.label;
                      setCreateForm((p) => ({ ...p, route_pickup: label }));
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <LocationOnOutlinedIcon sx={{ fontSize: 14, color: "#2f3891", mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b", lineHeight: 1.4 }}>{option.line1}</Typography>
                          {option.line2 && <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.3 }}>{option.line2}</Typography>}
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Start typing address…" sx={inputSx}
                      InputProps={{ ...params.InputProps, endAdornment: (
                        <>
                          {pickupLoading ? <CircularProgress size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )}}
                    />
                  )}
                />
              </Box>
              <Box>
                <Typography sx={labelSx}>Drop-off Location</Typography>
                <Autocomplete
                  freeSolo
                  options={dropoffOptions}
                  getOptionLabel={(opt) => typeof opt === "string" ? opt : opt.label}
                  loading={dropoffLoading}
                  inputValue={cf.route_dropoff}
                  filterOptions={(x) => x}
                  onInputChange={(_, value) => {
                    setCreateForm((p) => ({ ...p, route_dropoff: value }));
                    searchAddress(value, setDropoffOptions, setDropoffLoading, dropoffTimer);
                  }}
                  onChange={(_, value) => {
                    if (value) {
                      const label = typeof value === "string" ? value : value.label;
                      setCreateForm((p) => ({ ...p, route_dropoff: label }));
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <LocationOnOutlinedIcon sx={{ fontSize: 14, color: "#2f3891", mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b", lineHeight: 1.4 }}>{option.line1}</Typography>
                          {option.line2 && <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.3 }}>{option.line2}</Typography>}
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Start typing address…" sx={inputSx}
                      InputProps={{ ...params.InputProps, endAdornment: (
                        <>
                          {dropoffLoading ? <CircularProgress size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )}}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* ── Notes ── */}
          <Box sx={cardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, pb: 1.5, borderBottom: "1px solid #f1f5f9" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <NoteAltOutlinedIcon sx={{ fontSize: 15, color: "#2f3891" }} />
              </Box>
              <Typography sx={sectionTitleSx}>Message / Notes</Typography>
            </Box>
            <TextField
              fullWidth multiline rows={4}
              placeholder="Enter any additional details, special requests, or notes…"
              value={cf.message}
              onChange={set("message")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 13, borderRadius: "10px", bgcolor: "#f9fafb",
                  "& fieldset": { borderColor: "#e5e7eb" },
                  "&:hover fieldset": { borderColor: "#2f3891" },
                  "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: "2px" },
                  "&.Mui-focused": { bgcolor: "#ffffff" },
                },
              }}
            />
          </Box>

          {/* ── Action buttons ── */}
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", pb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/enquiry/list")}
              sx={{
                textTransform: "none", fontSize: 13, borderRadius: "8px",
                borderColor: "#e5e7eb", color: "#374151", bgcolor: "#f3f4f6",
                px: 2.5, fontWeight: 600,
                "&:hover": { bgcolor: "#e5e7eb", borderColor: "#d1d5db" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={createSaving}
              startIcon={createSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : undefined}
              sx={{
                textTransform: "none", fontSize: 13, borderRadius: "8px",
                bgcolor: "#2f3891", px: 2.5, fontWeight: 600, boxShadow: "none",
                "&:hover": { bgcolor: "#252e78", boxShadow: "none" },
              }}
            >
              {createSaving ? "Saving…" : "Save Enquiry"}
            </Button>
          </Box>

        </Box>
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <PersonOutlineIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
        <Typography sx={{ fontSize: 16, color: "#94a3b8", fontWeight: 600 }}>Enquiry not found</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/enquiry/list")} startIcon={<ArrowBackIcon />}>
          Back to List
        </Button>
      </Box>
    );
  }

  const currentPipeline = PIPELINE.find((p) => p.value === lead.status) || PIPELINE[0];
  const name = `${lead.contact.firstname}${lead.contact.lastname ? " " + lead.contact.lastname : ""}`;
  const initials = (lead.contact.firstname[0] || "?").toUpperCase();

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Sticky top bar ── */}
      <Box sx={{
        position: "sticky", top: 0, zIndex: 100,
        bgcolor: "#ffffff", borderBottom: `1px solid ${BORDER}`,
        px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
      }}>
        <IconButton size="small" onClick={() => navigate("/enquiry/list")}
          sx={{ bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#e2e8f0" }, borderRadius: 1.5 }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>{name}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.2, bgcolor: "#f1f5f9", borderRadius: 1 }}>
              <TagIcon sx={{ fontSize: 10, color: "#94a3b8" }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#2f3891", fontFamily: "monospace" }}>{lead.lead_ref}</Typography>
            </Box>
            <Box sx={{ px: 1, py: 0.25, bgcolor: currentPipeline.bg, border: `1px solid ${currentPipeline.border}`, borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: currentPipeline.color }}>{currentPipeline.label}</Typography>
            </Box>
            {lead.form_type === "charter_quote" && (
              <Box sx={{ px: 1, py: 0.25, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 1.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#dc2626" }}>Charter Quote</Typography>
              </Box>
            )}
          </Box>
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Created {dayjs(lead.created_at).fromNow()}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {lead.status !== "converted" && (
            <Button size="small" variant="outlined" startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
              disabled={converting} onClick={handleConvert}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, borderColor: "#16a34a", color: "#16a34a",
                "&:hover": { bgcolor: "#f0fdf4", borderColor: "#15803d" } }}>
              {converting ? "Converting…" : "Convert to Customer"}
            </Button>
          )}
          <Button size="small" variant="contained" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={handleConvertToQuote}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" } }}>
            Create Quote
          </Button>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ display: "flex", gap: 2.5, p: 3, maxWidth: 1400, mx: "auto", alignItems: "flex-start" }}>

        {/* ── LEFT COLUMN ── */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Message card */}
          {lead.contact.message && (
            <Box sx={{ bgcolor: "#ffffff", borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 3,
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 4, height: 18, bgcolor: "#2f3891", borderRadius: 2 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Enquiry Message
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 1.7 }}>{lead.contact.message}</Typography>
            </Box>
          )}

          {/* Charter quote details — Book Now leads only */}
          {lead.form_type === "charter_quote" && lead.custom_fields && (
            <CharterQuoteCard fields={lead.custom_fields} />
          )}

          {/* Pipeline */}
          <Box sx={{ bgcolor: "#ffffff", borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 3,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 2.5 }}>
              Pipeline Status
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              {PIPELINE.map((step, i) => (
                <PipelineStep
                  key={step.value}
                  step={step}
                  currentStatus={lead.status}
                  index={i}
                  total={PIPELINE.length}
                  onClick={() => handleStatusChange(step.value)}
                />
              ))}
            </Box>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 1.5, textAlign: "center" }}>
              Click a step to change status
            </Typography>
          </Box>

          {/* Quick actions */}
          <Box sx={{ bgcolor: "#ffffff", borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 3,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: activeAction ? 2 : 0 }}>
              {(["note", "call", "email", "sms"] as const).map((type) => {
                const cfg = COMM_CONFIG[type];
                const isActive = activeAction === type;
                return (
                  <Button key={type} size="small"
                    startIcon={React.cloneElement(cfg.icon as React.ReactElement, { sx: { fontSize: 14 } })}
                    onClick={() => { setActiveAction(isActive ? null : type); setCommSubject(""); setCommMsg(""); setSelectedNoteTemplateId(""); }}
                    variant={isActive ? "contained" : "outlined"}
                    sx={{
                      textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: 2, px: 1.5,
                      bgcolor: isActive ? cfg.color : "transparent",
                      borderColor: isActive ? cfg.color : BORDER,
                      color: isActive ? "#ffffff" : cfg.color,
                      "&:hover": { bgcolor: isActive ? cfg.color : cfg.bg, borderColor: cfg.color },
                    }}>
                    {cfg.label}
                  </Button>
                );
              })}
            </Box>
            {activeAction && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", mt: 1.5 }}>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {activeAction === "note" && (
                    <Select
                      size="small"
                      displayEmpty
                      value={selectedNoteTemplateId}
                      onChange={(e) => {
                        const tplId = e.target.value as string;
                        setSelectedNoteTemplateId(tplId);
                        if (tplId) {
                          const tpl = noteTemplates.find((t) => String(t.id) === tplId);
                          if (tpl) setCommMsg(tpl.note_message);
                        }
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, fontSize: 13, borderRadius: 2 }}
                    >
                      <MenuItem value=""><em style={{ color: "#94a3b8", fontStyle: "normal" }}>Select Note Template</em></MenuItem>
                      {noteTemplates.map((t) => (
                        <MenuItem key={t.id} value={String(t.id)}>{t.note_title}</MenuItem>
                      ))}
                    </Select>
                  )}
                  {activeAction === "email" && (
                    <TextField
                      fullWidth size="small"
                      value={commSubject} onChange={(e) => setCommSubject(e.target.value)}
                      placeholder="Subject…"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                    />
                  )}
                  <TextField
                    fullWidth multiline rows={2} size="small"
                    value={commMsg} onChange={(e) => setCommMsg(e.target.value)}
                    placeholder={activeAction === "email" ? "Email body…" : activeAction === "sms" ? "SMS message…" : `Log a ${COMM_CONFIG[activeAction].label.toLowerCase()} note…`}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                </Box>
                <Button
                  variant="contained" size="small"
                  disabled={!commMsg.trim() || savingComm}
                  onClick={handleLogComm}
                  startIcon={savingComm ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <SendIcon sx={{ fontSize: 14 }} />}
                  sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, height: 36, px: 2,
                    bgcolor: COMM_CONFIG[activeAction].color, "&:hover": { bgcolor: COMM_CONFIG[activeAction].color, filter: "brightness(0.9)" } }}>
                  {activeAction === "email" ? "Send" : activeAction === "sms" ? "Send" : "Log"}
                </Button>
              </Box>
            )}
          </Box>

          {/* Communication history */}
          <Box sx={{ bgcolor: "#ffffff", borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 3,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Activity History
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                {(lead.communications?.length || 0) + (lead.statusHistory?.length || 0)} entries
              </Typography>
            </Box>

            {/* Merge communications + status history, sort by created_at desc */}
            {(() => {
              const allEvents = [
                ...(lead.communications || []).map((c) => ({ ...c, _type: "comm" as const })),
                ...(lead.statusHistory  || []).map((h) => ({ ...h, _type: "status" as const })),
              ].sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)));

              if (allEvents.length === 0) {
                return (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No activity yet.</Typography>
                  </Box>
                );
              }

              return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {allEvents.map((ev, i) => {
                    if (ev._type === "comm") {
                      const c = ev as ILeadCommunication & { _type: "comm" };
                      const cfg = COMM_CONFIG[c.type];
                      return (
                        <Box key={`c-${i}`} sx={{ display: "flex", gap: 2, py: 1.5,
                          borderBottom: i < allEvents.length - 1 ? `1px solid #f8fafc` : "none" }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: cfg.bg,
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: cfg.color }}>
                            {cfg.icon}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                              <Box sx={{ px: 0.75, py: 0.1, bgcolor: cfg.bg, borderRadius: 1 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: cfg.color }}>{cfg.label}</Typography>
                              </Box>
                              {c.sent_by && <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>by {c.sent_by}</Typography>}
                              <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: "auto", flexShrink: 0 }}>
                                {dayjs(c.created_at).fromNow()}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>{c.message}</Typography>
                          </Box>
                        </Box>
                      );
                    } else {
                      const h = ev as any;
                      const nxt = PIPELINE.find((p) => p.value === h.new_status);
                      return (
                        <Box key={`h-${i}`} sx={{ display: "flex", gap: 2, py: 1.5,
                          borderBottom: i < allEvents.length - 1 ? `1px solid #f8fafc` : "none" }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: nxt?.bg || "#f8fafc",
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: nxt?.color || "#94a3b8" }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                              <Typography sx={{ fontSize: 12, color: "#374151" }}>
                                {h.old_status
                                  ? <>{PIPELINE.find((p) => p.value === h.old_status)?.label ?? h.old_status} → <strong style={{ color: nxt?.color }}>{nxt?.label ?? h.new_status}</strong></>
                                  : <>Lead created as <strong style={{ color: nxt?.color }}>{nxt?.label}</strong></>
                                }
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: "auto", flexShrink: 0 }}>
                                {dayjs(h.created_at).fromNow()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                  })}
                </Box>
              );
            })()}
          </Box>
        </Box>

        {/* ── RIGHT COLUMN ── */}
        <Box sx={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Customer profile card */}
          <Box sx={{ bgcolor: "#ffffff", borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 2.5,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mb: 2 }}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: "#eef2ff", color: "#2f3891",
                fontSize: 22, fontWeight: 800, borderRadius: 2 }}>
                {initials}
              </Avatar>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>{name}</Typography>
              {lead.source && (
                <Box sx={{ px: 1, py: 0.2, bgcolor: "#f1f5f9", borderRadius: 1 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                    via {lead.source}
                  </Typography>
                </Box>
              )}
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {lead.contact.email && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <EmailOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, color: "#374151" }} noWrap>{lead.contact.email}</Typography>
                </Box>
              )}
              {lead.contact.phone && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PhoneOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, color: "#374151" }}>{lead.contact.phone}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Event details card */}
          {(lead.contact.event_type || lead.contact.event_date || lead.contact.route_pickup || lead.contact.route_dropoff) && (
            <Box sx={{ bgcolor: "#ffffff", borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 2.5,
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                Event Details
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {lead.contact.event_type && (
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>Type</Typography>
                    <Box sx={{ display: "inline-flex", px: 1, py: 0.3, bgcolor: "#eff6ff", borderRadius: 1, border: "1px solid #bfdbfe" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#2563eb" }}>
                        {EVENT_TYPE_LABELS[lead.contact.event_type] ?? lead.contact.event_type}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {lead.contact.event_date && (
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>Date</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: "#64748b" }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {dayjs(lead.contact.event_date).format("MMM D, YYYY")}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        ({dayjs(lead.contact.event_date).fromNow()})
                      </Typography>
                    </Box>
                  </Box>
                )}
                {(lead.contact.route_pickup || lead.contact.route_dropoff) && (
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>Route</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <RouteOutlinedIcon sx={{ fontSize: 13, color: "#64748b" }} />
                      <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
                        {lead.contact.route_pickup || "—"}
                        {lead.contact.route_dropoff && <> → {lead.contact.route_dropoff}</>}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Lead meta card */}
          <Box sx={{ bgcolor: "#ffffff", borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 2.5,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
              Lead Info
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[
                { label: "Reference", value: lead.lead_ref },
                { label: "Status",    value: PIPELINE.find((p) => p.value === lead.status)?.label || lead.status },
                { label: "Created",   value: dayjs(lead.created_at).format("MMM D, YYYY h:mm A") },
                { label: "Updated",   value: dayjs(lead.updated_at).fromNow() },
                ...(lead.notes ? [{ label: "Notes", value: lead.notes }] : []),
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#374151", mt: 0.15, lineHeight: 1.5 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Converted customer */}
          {lead.customer && (
            <Box sx={{ bgcolor: "#f0fdf4", borderRadius: 2.5, border: "1px solid #bbf7d0", p: 2.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75 }}>
                ✓ Converted to Customer
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#14532d" }}>{lead.customer.name}</Typography>
              {lead.customer.email && <Typography sx={{ fontSize: 12, color: "#16a34a" }}>{lead.customer.email}</Typography>}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EnquiryDetail;
